import type { SideNavIssue } from "../components/StepSideNav";
import type { ActionPlanAction, ActionPlanGoal, ActionPlanThematic } from "../api/types";
import { goalName } from "./selection";

/**
 * Build the side-nav model from a plan's thematics.
 *
 * Steps 2 and 4 index the same tree but tally different things — retained
 * actions there, actions carrying planning detail here — so the caller supplies
 * which actions of a goal count (`actionsOf`) and how to phrase the tally
 * (`tally`). Goals and issues left with nothing are dropped, since a nav entry
 * that leads to an empty block is just a dead link.
 */
export function buildNavIssues(
  thematics: ActionPlanThematic[],
  {
    actionsOf,
    tally,
  }: {
    actionsOf: (goal: ActionPlanGoal) => ActionPlanAction[];
    tally: (actions: ActionPlanAction[]) => { count: string; warn?: boolean };
  },
): SideNavIssue[] {
  return thematics.flatMap((thematic) =>
    thematic.action_plan_issues.flatMap((issue) => {
      const goals = issue.action_plan_goals
        .map((goal) => ({ goal, actions: actionsOf(goal) }))
        .filter((entry) => entry.actions.length > 0);
      if (goals.length === 0) return [];
      return [
        {
          id: issue.id,
          name: issue.name,
          cssClass: thematic.css_class,
          ...tally(goals.flatMap((entry) => entry.actions)),
          goals: goals.map(({ goal, actions }) => ({
            id: goal.id,
            label: goalName(goal),
            ...tally(actions),
          })),
        },
      ];
    }),
  );
}
